import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { StatusBadge, LifecycleBadge, RelationshipStageBadge, stageAccent, formatDate } from "@/components/prospects/shared";
import { formatPhone } from "@/lib/phone";
import ContactDialog from "@/components/prospects/ContactDialog";
import PropertyDialog from "@/components/prospects/PropertyDialog";
import BulkImportDialog from "@/components/prospects/BulkImportDialog";
import ReassignDialog from "@/components/prospects/ReassignDialog";
import EditProspectDialog from "@/components/prospects/EditProspectDialog";
import ActivityDialog from "@/components/activities/ActivityDialog";
import ActivityTimeline from "@/components/activities/ActivityTimeline";
import OpportunityDialog from "@/components/opportunities/OpportunityDialog";
import { StageBadge, OutcomeBadge, money } from "@/components/opportunities/shared";
import {
  ArrowLeft, CircleNotch, PencilSimple, Plus, Star, UserSwitch, Buildings,
  PhoneCall, Handshake, Paperclip, Archive, ArrowCounterClockwise, MapPin, UploadSimple, Printer,
} from "@phosphor-icons/react";
import { AttachmentsTab } from "@/components/prospects/AttachmentsTab";
import { ContactDrawer } from "@/components/prospects/ContactDrawer";
import { RichTextView, RichTextEditor } from "@/components/ui/rich-text";

async function fetchDetailInto(setProspect, prospectId) {
  try {
    const { data } = await api.get(`/prospects/${prospectId}`);
    setProspect(data);
  } catch (e) {
    toast.error(formatApiError(e.response?.data?.detail));
    setProspect(undefined);
  }
}

async function fetchLookupsInto(setLookups) {
  try {
    const [sources, temps, classifications, segments, occupancies, users, serviceStatuses] = await Promise.all([
      api.get("/reference/lead-sources"),
      api.get("/reference/lead-temperatures"),
      api.get("/reference/contact-classifications"),
      api.get("/reference/market-segments"),
      api.get("/reference/occupancy-classifications"),
      api.get("/users/options"),
      api.get("/reference/service-statuses"),
    ]);
    setLookups({
      sources: sources.data, temps: temps.data, classifications: classifications.data,
      segments: segments.data, occupancies: occupancies.data, users: users.data,
      serviceStatuses: serviceStatuses.data,
    });
  } catch (e) {
    // Reference lookups are non-critical for viewing the record itself.
    toast.error("Reference data could not be loaded — some labels may be missing. Please refresh.");
  }
}

async function fetchProspectActivitiesInto(setActivities, prospectId) {
  try {
    const { data } = await api.get("/activities", { params: { prospect_id: prospectId } });
    setActivities(data);
  } catch (e) {
    toast.error("Activities could not be loaded. Please refresh.");
  }
}

async function fetchProspectOpportunitiesInto(setOpportunities, prospectId) {
  try {
    const { data } = await api.get("/opportunities", { params: { prospect_id: prospectId } });
    setOpportunities(data);
  } catch (e) {
    toast.error("Opportunities could not be loaded. Please refresh.");
  }
}

function HeaderStat({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#6D6D70]">{label}</p>
      <div className="mt-1 text-sm font-medium text-[#09090B]">{children}</div>
    </div>
  );
}

function InfoRow({ label, value, testId }) {
  return (
    <div className="grid grid-cols-3 gap-3 border-b border-[#F4F4F5] py-2.5 text-sm last:border-0">
      <span className="text-xs font-bold uppercase tracking-wide text-[#6D6D70]">{label}</span>
      <span className="col-span-2 break-words text-[#09090B]" data-testid={testId}>{value || "—"}</span>
    </div>
  );
}

function EmptyTab({ icon: Icon, title, note }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-sm border border-dashed border-[#E4E4E7] bg-white py-14 text-center">
      <Icon size={30} className="text-[#032852]/30" />
      <p className="text-sm font-semibold text-[#032852]">{title}</p>
      <p className="max-w-md text-xs text-[#6D6D70]">{note}</p>
    </div>
  );
}

export default function ProspectDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "overview";
  const navigate = useNavigate();
  const location = useLocation();
  // Entered from Clients keeps Clients navigation; the return path survives
  // organization tabs and opportunity detail via this state flag.
  const fromClients = location.state?.from === "clients";
  const { isAdmin } = useAuth();
  const [prospect, setProspect] = useState(null);
  const [lookups, setLookups] = useState({ sources: [], temps: [], classifications: [], segments: [], occupancies: [], users: [] });
  const [editOpen, setEditOpen] = useState(false);

  // "Needs review" cues deep-link here with ?review=stage — open the edit form
  // at the Lifecycle Stage field so the user can resolve it immediately.
  useEffect(() => {
    if (prospect && new URLSearchParams(location.search).get("review") === "stage") setEditOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prospect?.id, location.search]);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [contactDialog, setContactDialog] = useState(null); // null | {contact?}
  const [contactDrawer, setContactDrawer] = useState(null); // Batch H: contact detail view
  const [intelEdit, setIntelEdit] = useState(null); // Batch H: null | {html} while editing
  const [propertyDialog, setPropertyDialog] = useState(null); // null | {property?}
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [activities, setActivities] = useState([]);
  const [activityDialog, setActivityDialog] = useState(null); // {activity?, completeOf?, presetContactId?, presetPropertyId?}
  const [opportunities, setOpportunities] = useState([]);
  const [oppDialogOpen, setOppDialogOpen] = useState(false);

  const helpers = useRef({ fetchDetailInto, fetchLookupsInto, fetchProspectActivitiesInto, fetchProspectOpportunitiesInto });
  const load = useCallback(() => helpers.current.fetchDetailInto(setProspect, id), [helpers, setProspect, id]);
  useEffect(() => {
    load();
  }, [load]);
  const loadActivities = useCallback(() => helpers.current.fetchProspectActivitiesInto(setActivities, id), [helpers, setActivities, id]);
  useEffect(() => {
    loadActivities();
  }, [loadActivities]);
  const loadOpportunities = useCallback(() => helpers.current.fetchProspectOpportunitiesInto(setOpportunities, id), [helpers, setOpportunities, id]);
  useEffect(() => {
    loadOpportunities();
  }, [loadOpportunities]);
  const loadLookups = useCallback(() => helpers.current.fetchLookupsInto(setLookups), [helpers, setLookups]);
  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  if (prospect === null) {
    return (
      <div className="flex items-center justify-center py-24">
        <CircleNotch size={26} className="animate-spin text-[#032852]" />
      </div>
    );
  }
  if (!prospect) {
    return (
      <div className="p-8 text-center text-sm text-[#6D6D70]">
        Prospect not found or not accessible.
        <Button variant="link" onClick={() => navigate("/prospects")}>Back to prospects</Button>
      </div>
    );
  }

  const setPrimary = async (contactId) => {
    try {
      await api.post(`/prospects/${id}/primary-contact`, { contact_id: contactId });
      toast.success("Primary contact updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const setContactStatus = async (contact, status) => {
    try {
      await api.patch(`/contacts/${contact.id}`, { status });
      toast.success(status === "archived" ? "Contact archived" : "Contact restored");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const setPropertyStatus = async (prop, status) => {
    try {
      await api.patch(`/properties/${prop.id}`, { status });
      toast.success(status === "archived" ? "Property archived" : "Property restored");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // Legacy stored ids stay readable: they resolve to their standardized replacement's name.
  const segmentName = (sid) => lookups.segments.find((s) => s.id === sid || (s.legacy_ids || []).includes(sid))?.name;
  const occupancyName = (oid) => lookups.occupancies.find((o) => o.id === oid || (o.legacy_ids || []).includes(oid))?.name;
  const contactName = (cid) => {
    const c = (prospect.contacts || []).find((x) => x.id === cid);
    return c ? `${c.first_name} ${c.last_name}` : "—";
  };
  const originator = prospect.lead_originator || {};

  return (
    <div className="p-6 sm:p-8" data-testid="prospect-detail-page">
      <button onClick={() => navigate(fromClients ? "/clients" : "/prospects")} className="mb-4 flex items-center gap-1.5 text-sm font-semibold text-[#032852] hover:text-[#569A1F]" data-testid={fromClients ? "back-to-clients" : "back-to-prospects"}>
        <ArrowLeft size={16} weight="bold" /> {fromClients ? "Back to Clients" : "Prospects"}
      </button>

      {/* Header — thin lifecycle accent bar on top; content and layout unchanged */}
      <div className="overflow-hidden rounded-sm border border-[#E4E4E7] bg-white">
        <div className="h-1.5 w-full" data-testid="header-stage-accent"
          style={{ backgroundColor: stageAccent(prospect.relationship_stage, prospect.lifecycle_status) }} />
        <div className="p-5 sm:p-6">
        <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-start">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-heading text-2xl font-black tracking-tight text-[#032852] sm:text-3xl" data-testid="prospect-org-name">
                {prospect.organization_name}
              </h1>
              <LifecycleBadge status={prospect.lifecycle_status} />
              {prospect.lifecycle_status !== "client" && (
                <RelationshipStageBadge stage={prospect.relationship_stage}
                  reviewPending={prospect.stage_review_pending} testId="header-stage"
                  onReviewClick={() => setEditOpen(true)} />
              )}
              <StatusBadge status={prospect.status} />
              {prospect.duplicate_flag && isAdmin && (
                <Badge className="rounded-sm border-0 bg-[#F59E0B]/15 text-xs font-semibold text-[#B45309] hover:bg-[#F59E0B]/15" data-testid="header-duplicate-flag">
                  Duplicate review
                </Badge>
              )}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <HeaderStat label="Assigned salesperson" testId="header-salesperson">{prospect.assigned_salesperson_name || "—"}</HeaderStat>
              <HeaderStat label="Lead source" testId="header-source">{prospect.lead_source_name || "—"}</HeaderStat>
              <HeaderStat label="Lead originator" testId="header-originator">
                {prospect.lead_originator_display || "—"}
                {originator.type === "external" && <span className="ml-1 text-xs text-[#6D6D70]">(external)</span>}
              </HeaderStat>
              <HeaderStat label="Date added" testId="header-date">{formatDate(prospect.date_entered)}</HeaderStat>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button variant="outline" onClick={() => navigate(`/reports?prospect=${prospect.id}`)}
              className="rounded-sm text-[#032852]" data-testid="prepare-report-btn">
              <Printer size={16} /> Prepare Report
            </Button>
            {isAdmin && (
              <Button variant="outline" onClick={() => setReassignOpen(true)} className="rounded-sm text-[#032852]" data-testid="reassign-btn">
                <UserSwitch size={16} /> Reassign
              </Button>
            )}
            <Button onClick={() => setEditOpen(true)} className="rounded-sm bg-[#032852] font-semibold text-white hover:bg-[#032852]/90" data-testid="edit-prospect-btn">
              <PencilSimple size={16} /> Edit
            </Button>
          </div>
        </div>

        {/* Actionable review items — each names the exact field and how to resolve it */}
        {prospect.lifecycle_status !== "client" &&
          (!prospect.relationship_stage || prospect.stage_review_pending) && (
          <div className="mt-4 rounded-sm border border-dashed border-[#B45309]/50 bg-[#F59E0B]/5 p-3" data-testid="review-items">
            <p className="text-xs font-bold uppercase tracking-wide text-[#B45309]">Needs review</p>
            <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs text-[#52525B]">
              {!prospect.relationship_stage && (
                <li data-testid="review-item-stage">
                  <span className="font-semibold">Lifecycle Stage is not set.</span> Select Cold, Warm, or Hot based on documented relationship evidence.
                </li>
              )}
              {prospect.stage_review_pending && (
                <li data-testid="review-item-closed-opp">
                  <span className="font-semibold">An opportunity closed.</span> Confirm the current Lifecycle Stage is still accurate — it was retained, not changed automatically.
                </li>
              )}
            </ul>
            <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}
              className="mt-2 h-7 rounded-sm border-[#B45309]/40 text-xs font-semibold text-[#B45309] hover:bg-[#F59E0B]/10"
              data-testid="review-set-stage-btn">
              Set Lifecycle Stage
            </Button>
          </div>
        )}
        </div>
      </div>

      {/* Tabs — ?tab= deep-links straight to a section (e.g. Service Locations from market search) */}
      <Tabs defaultValue={initialTab} className="mt-6">
        <TabsList className="h-auto w-full justify-start gap-1 overflow-x-auto rounded-sm bg-[#F4F4F5] p-1">
          <TabsTrigger value="overview" className="rounded-sm text-xs font-bold uppercase tracking-wide" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="contacts" className="rounded-sm text-xs font-bold uppercase tracking-wide" data-testid="tab-contacts">
            Contacts ({(prospect.contacts || []).length})
          </TabsTrigger>
          <TabsTrigger value="properties" className="rounded-sm text-xs font-bold uppercase tracking-wide" data-testid="tab-properties">
            Properties ({(prospect.properties || []).length})
          </TabsTrigger>
          <TabsTrigger value="activities" className="rounded-sm text-xs font-bold uppercase tracking-wide" data-testid="tab-activities">
            Activities ({activities.length})
          </TabsTrigger>
          <TabsTrigger value="opportunities" className="rounded-sm text-xs font-bold uppercase tracking-wide" data-testid="tab-opportunities">
            Opportunities ({opportunities.length})
          </TabsTrigger>
          <TabsTrigger value="attachments" className="rounded-sm text-xs font-bold uppercase tracking-wide" data-testid="tab-attachments">Attachments</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-sm border border-[#E4E4E7] bg-white p-5">
              <h3 className="mb-2 text-xs font-bold uppercase tracking-[0.15em] text-[#032852]">Primary Contact</h3>
              {(() => {
                const pc = (prospect.contacts || []).find((c) => c.id === prospect.primary_contact_id);
                return pc ? (
                  <div data-testid="overview-primary-contact">
                    <InfoRow label="Name" value={`${pc.first_name} ${pc.last_name}`} testId="overview-pc-name" />
                    <InfoRow label="Role / Title" value={pc.title} />
                    <InfoRow label="Email" value={pc.email} testId="overview-pc-email" />
                    <InfoRow label="Phone" value={formatPhone(pc.office_phone || pc.mobile_phone)} testId="overview-pc-phone" />
                  </div>
                ) : <p className="py-2 text-xs text-[#6D6D70]" data-testid="overview-primary-contact">No primary contact designated yet — set one under Contacts.</p>;
              })()}
              <h3 className="mb-2 mt-4 border-t border-[#F4F4F5] pt-4 text-xs font-bold uppercase tracking-[0.15em] text-[#032852]">Organization</h3>
              <InfoRow label="Website" value={prospect.website} testId="overview-website" />
              <InfoRow label="LinkedIn" value={prospect.linkedin_url} />
              <InfoRow label="Office phone" value={formatPhone(prospect.general_office_phone)} />
              <InfoRow label="General email" value={prospect.general_email} />
              <p className="pt-1 text-[10px] text-[#9CA3AF]">Company office phone/email is kept distinct from the contact's personal details.</p>
              <div className="py-2 text-sm" data-testid="overview-notes">
                <p className="text-xs font-bold uppercase tracking-wide text-[#6D6D70]">Notes</p>
                {/* Plain-text notes keep their original line breaks */}
                <p className="mt-0.5 whitespace-pre-wrap text-[#3F3F46]">{prospect.notes || "—"}</p>
              </div>
              <div className="mt-2 border-t border-[#F4F4F5] pt-3" data-testid="overview-intelligence">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#032852]">Prospect Intelligence</h3>
                  <button onClick={() => setIntelEdit({ html: prospect.intelligence_html || "" })}
                    className="text-xs font-semibold text-[#032852] hover:text-[#569A1F]" data-testid="edit-intelligence-btn">
                    <PencilSimple size={13} className="mr-0.5 inline" /> {prospect.intelligence_html ? "Edit" : "Add"}
                  </button>
                </div>
                {intelEdit ? (
                  <div className="mt-2 space-y-2">
                    <RichTextEditor value={intelEdit.html} onChange={(html) => setIntelEdit({ html })}
                      testId="prospect-intel-editor" />
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs" onClick={() => setIntelEdit(null)}>Cancel</Button>
                      <Button size="sm" className="h-7 rounded-sm bg-[#569A1F] text-xs font-semibold text-white hover:bg-[#457d18]"
                        data-testid="save-intelligence-btn"
                        onClick={async () => {
                          try {
                            await api.patch(`/prospects/${id}`, { intelligence_html: intelEdit.html });
                            toast.success("Prospect Intelligence saved");
                            setIntelEdit(null);
                            load();
                          } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                        }}>Save</Button>
                    </div>
                  </div>
                ) : prospect.intelligence_html ? (
                  <RichTextView html={prospect.intelligence_html} testId="prospect-intel-view" className="mt-1" />
                ) : (
                  <p className="mt-1 text-xs text-[#9CA3AF]">Organized research — headings, bullets and emphasis — kept separate from day-to-day notes.</p>
                )}
              </div>
            </div>
            <div className="rounded-sm border border-[#E4E4E7] bg-white p-5">
              <h3 className="mb-2 text-xs font-bold uppercase tracking-[0.15em] text-[#032852]">Lead & Ownership</h3>
              <InfoRow label="Lead source" value={prospect.lead_source_name} />
              <InfoRow label="Source detail" value={prospect.lead_source_detail} testId="overview-source-detail" />
              <InfoRow label="Originator" value={
                originator.type === "external"
                  ? [originator.name, originator.organization].filter(Boolean).join(" — ")
                  : prospect.lead_originator_display
              } />
              <InfoRow label="Original creator" value={prospect.original_creator_name} />
              <InfoRow label="Entered CRM" value={formatDate(prospect.date_entered)} />
              <div className="mt-3">
                <p className="text-xs font-bold uppercase tracking-wide text-[#6D6D70]">Ownership history</p>
                <div className="mt-2 space-y-2" data-testid="assignment-history">
                  {(prospect.assignment_history || []).slice().reverse().map((h) => (
                    <div key={`${h.assigned_at}-${h.assigned_salesperson_id}`} className="rounded-sm bg-[#F4F4F5] p-2.5 text-xs text-[#52525B]">
                      <span className="font-semibold text-[#032852]">
                        {lookups.users.find((u) => u.id === h.assigned_salesperson_id)?.name || h.assigned_salesperson_id}
                      </span>
                      {" — "}{formatDate(h.assigned_at)}{h.note ? ` · ${h.note}` : ""}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="contacts" className="mt-4">
          <div className="mb-3 flex justify-end">
            <Button onClick={() => setContactDialog({})} className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]" data-testid="add-contact-btn">
              <Plus size={16} weight="bold" /> Add Contact
            </Button>
          </div>
          <div className="grid gap-3 lg:grid-cols-2">
            {(prospect.contacts || []).map((c) => (
              <div key={c.id} className={`rounded-sm border bg-white p-4 ${c.status === "archived" ? "opacity-60" : ""} ${prospect.primary_contact_id === c.id ? "border-[#569A1F]" : "border-[#E4E4E7]"}`}
                   data-testid={`contact-card-${c.first_name.toLowerCase()}-${c.last_name.toLowerCase()}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="flex items-center gap-1.5 font-semibold text-[#09090B]">
                      <button onClick={() => setContactDrawer(c)} className="truncate text-left hover:text-[#569A1F] hover:underline"
                        data-testid={`open-contact-${c.id}`}>
                        {c.first_name} {c.last_name}
                      </button>
                      {prospect.primary_contact_id === c.id && (
                        <span className="flex items-center gap-1 rounded-sm bg-[#569A1F]/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-[#457d18]" data-testid="primary-badge">
                          <Star size={10} weight="fill" /> Primary
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-[#6D6D70]">{c.title || "—"}</p>
                  </div>
                  <StatusBadge status={c.status} />
                </div>
                <div className="mt-1.5" title="Cue derived from the parent organization's relationship stage">
                  <RelationshipStageBadge stage={prospect.relationship_stage} lifecycle={prospect.lifecycle_status} testId={`contact-stage-${c.id}`} />
                </div>
                <div className="mt-3 space-y-1 text-xs text-[#52525B]">
                  {c.office_phone && <p>Office: {formatPhone(c.office_phone)}</p>}
                  {c.mobile_phone && <p>Mobile: {formatPhone(c.mobile_phone)}</p>}
                  {c.email && <p>{c.email}</p>}
                </div>
                {(c.classifications || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {c.classifications.map((cl) => (
                      <span key={cl} className="rounded-sm bg-[#032852]/5 px-1.5 py-0.5 text-[10px] font-semibold text-[#032852]">{cl}</span>
                    ))}
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-2 border-t border-[#F4F4F5] pt-3 text-xs">
                  <button className="font-semibold text-[#032852] hover:text-[#569A1F]" onClick={() => setActivityDialog({ presetContactId: c.id })} data-testid={`log-activity-contact-${c.id}`}>Log activity</button>
                  <button className="font-semibold text-[#032852] hover:text-[#569A1F]" onClick={() => setContactDialog({ contact: c })} data-testid={`edit-contact-${c.id}`}>Edit</button>
                  {prospect.primary_contact_id !== c.id && c.status !== "archived" && (
                    <button className="font-semibold text-[#032852] hover:text-[#569A1F]" onClick={() => setPrimary(c.id)} data-testid={`make-primary-${c.id}`}>Make primary</button>
                  )}
                  {prospect.primary_contact_id !== c.id && (
                    c.status !== "archived" ? (
                      <button className="font-semibold text-[#B91C1C] hover:text-[#EF4444]" onClick={() => setContactStatus(c, "archived")} data-testid={`archive-contact-${c.id}`}>Archive</button>
                    ) : (
                      <button className="font-semibold text-[#569A1F]" onClick={() => setContactStatus(c, "active")}>Restore</button>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="properties" className="mt-4">
          <div className="mb-3 flex justify-end gap-2">
            <Button variant="outline" onClick={() => navigate(`/reports?prospect=${prospect.id}&section=properties`)}
              className="rounded-sm font-semibold text-[#032852]" data-testid="properties-report-btn">
              <Printer size={16} /> Property Report
            </Button>
            <Button variant="outline" onClick={() => setBulkImportOpen(true)} className="rounded-sm font-semibold" data-testid="bulk-import-btn">
              <UploadSimple size={16} weight="bold" /> Bulk Import Properties
            </Button>
            <Button onClick={() => setPropertyDialog({})} className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]" data-testid="add-property-btn">
              <Plus size={16} weight="bold" /> Add Property
            </Button>
          </div>
          {(prospect.properties || []).length === 0 ? (
            <EmptyTab icon={Buildings} title="No properties yet"
                      note="A prospect may have no identified property, a single location, or an entire portfolio. Add the first one when known." />
          ) : (
            <div className="grid gap-3 lg:grid-cols-2">
              {prospect.properties.map((pr) => (
                <div key={pr.id} className={`rounded-sm border border-[#E4E4E7] bg-white p-4 ${pr.status === "archived" ? "opacity-60" : ""}`}
                     data-testid={`property-card-${pr.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-[#09090B]">{pr.name}</p>
                      <p className="mt-0.5 flex items-start gap-1 text-xs text-[#6D6D70]">
                        <MapPin size={13} className="mt-0.5 shrink-0" />
                        {[pr.street_address, pr.address_line2, pr.city, pr.state, pr.zip_code].filter(Boolean).join(", ") || "No address yet"}
                      </p>
                    </div>
                    <StatusBadge status={pr.status} />
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5 text-[10px] font-semibold">
                    {pr.county && <span className="rounded-sm bg-[#F4F4F5] px-1.5 py-0.5 text-[#52525B]">County: {pr.county}</span>}
                    {pr.square_footage != null && pr.square_footage !== "" && (
                      <span className="rounded-sm bg-[#F4F4F5] px-1.5 py-0.5 text-[#52525B]">{Number(pr.square_footage).toLocaleString()} sq ft</span>
                    )}
                    {segmentName(pr.market_segment_id) && (
                      <span className="rounded-sm bg-[#032852]/5 px-1.5 py-0.5 text-[#032852]">{segmentName(pr.market_segment_id)}</span>
                    )}
                    {occupancyName(pr.occupancy_classification_id) && (
                      <span className="rounded-sm bg-[#569A1F]/10 px-1.5 py-0.5 text-[#457d18]">{occupancyName(pr.occupancy_classification_id)}</span>
                    )}
                  </div>
                  {(pr.on_site_contacts || []).length > 0 && (
                    <div className="mt-2 space-y-0.5 text-xs text-[#52525B]">
                      {pr.on_site_contacts.map((sc) => (
                        <p key={`${sc.contact_id}-${sc.site_role || "site"}`}>{contactName(sc.contact_id)}{sc.site_role ? ` — ${sc.site_role}` : ""}</p>
                      ))}
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2 border-t border-[#F4F4F5] pt-3">
                    <Button size="sm" onClick={() => setActivityDialog({ presetPropertyId: pr.id })}
                      className="h-8 rounded-sm bg-[#569A1F] px-3 text-xs font-semibold text-white hover:bg-[#457d18]"
                      data-testid={`log-activity-property-${pr.id}`}>
                      <Plus size={14} weight="bold" /> Log Activity
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setPropertyDialog({ property: pr })}
                      className="h-8 rounded-sm border-[#032852]/30 px-3 text-xs font-semibold text-[#032852] hover:bg-[#032852]/5"
                      data-testid={`edit-property-${pr.id}`}>
                      <PencilSimple size={14} /> Edit Property
                    </Button>
                    {pr.status !== "archived" ? (
                      <Button size="sm" variant="outline" onClick={() => setPropertyStatus(pr, "archived")}
                        className="h-8 rounded-sm border-[#DC2626]/40 px-3 text-xs font-semibold text-[#B91C1C] hover:bg-[#DC2626]/5"
                        data-testid={`archive-property-${pr.id}`}>
                        <Archive size={14} /> Archive
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => setPropertyStatus(pr, "active")}
                        className="h-8 rounded-sm border-[#569A1F]/40 px-3 text-xs font-semibold text-[#457d18] hover:bg-[#569A1F]/5"
                        data-testid={`restore-property-${pr.id}`}>
                        <ArrowCounterClockwise size={14} /> Restore
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="activities" className="mt-4">
          <div className="mb-3 flex justify-end gap-2">
            <Button variant="outline" onClick={() => navigate(`/reports?prospect=${prospect.id}&section=activities`)}
              className="rounded-sm font-semibold text-[#032852]" data-testid="activities-report-btn">
              <Printer size={16} /> Activity Report
            </Button>
            <Button onClick={() => setActivityDialog({})} className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]" data-testid="log-activity-btn">
              <Plus size={16} weight="bold" /> Log Activity
            </Button>
          </div>
          <ActivityTimeline
            activities={activities}
            onDone={loadActivities}
            onEdit={(a) => setActivityDialog({ activity: a })}
            onCompleteAndLog={(a) => setActivityDialog({ completeOf: a })}
          />
        </TabsContent>
        <TabsContent value="opportunities" className="mt-4">
          <div className="mb-3 flex justify-end">
            <Button onClick={() => setOppDialogOpen(true)} className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]" data-testid="new-opportunity-tab-btn">
              <Plus size={16} weight="bold" /> New Opportunity
            </Button>
          </div>
          {opportunities.length === 0 ? (
            <EmptyTab icon={Handshake} title="No opportunities yet"
                      note="One organization can carry many opportunities — each with its own stage, financials and close dates." />
          ) : (
            <div className="space-y-2" data-testid="prospect-opportunities-list">
              {opportunities.map((o) => (
                <button key={o.id} onClick={() => navigate(`/opportunities/${o.id}`)}
                  className="flex w-full flex-wrap items-center justify-between gap-2 rounded-sm border border-[#E4E4E7] bg-white p-4 text-left transition-colors hover:border-[#032852]/40"
                  data-testid={`prospect-opp-${o.id}`}>
                  <div className="min-w-0">
                    <span className="flex items-center gap-2 text-sm font-semibold text-[#09090B]">
                      {o.name} <OutcomeBadge outcome={o.outcome} />
                    </span>
                    <p className="mt-0.5 text-xs text-[#6D6D70]">
                      {o.owner_name}{o.target_close_date ? ` · Target close ${formatDate(o.target_close_date)}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-heading text-sm font-black text-[#032852]">{money(o.first_12_month_revenue)}</span>
                    <StageBadge name={o.stage_name} />
                  </div>
                </button>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="attachments" className="mt-4">
          <AttachmentsTab prospectId={id} />
        </TabsContent>
      </Tabs>

      {contactDrawer && (
        <ContactDrawer contact={contactDrawer} prospect={prospect} activities={activities}
          onClose={() => setContactDrawer(null)}
          onEdit={() => setContactDialog({ contact: contactDrawer })} />
      )}

      {activityDialog !== null && (
        <ActivityDialog
          key={activityDialog.activity?.id || activityDialog.completeOf?.id || activityDialog.presetContactId || activityDialog.presetPropertyId || "new-activity"}
          open prospect={prospect} contacts={prospect.contacts || []} properties={prospect.properties || []}
          activity={activityDialog.activity} completeFollowUpOf={activityDialog.completeOf}
          presetContactId={activityDialog.presetContactId} presetPropertyId={activityDialog.presetPropertyId}
          onClose={() => setActivityDialog(null)} onDone={loadActivities}
        />
      )}
      {oppDialogOpen && (
        <OpportunityDialog key="new-prospect-opportunity" open presetProspectId={id}
          presetProspectName={prospect.organization_name}
          onClose={() => setOppDialogOpen(false)} onDone={loadOpportunities} />
      )}
      <EditProspectDialog open={editOpen} prospect={prospect} sources={lookups.sources}
                          onClose={() => setEditOpen(false)} onDone={load} />
      <ReassignDialog open={reassignOpen} prospect={prospect} users={lookups.users}
                      onClose={() => setReassignOpen(false)} onDone={load} />
      <ContactDialog key={contactDialog?.contact?.id || (contactDialog ? "new-contact" : "closed-contact")}
                     open={contactDialog !== null} prospectId={id} contact={contactDialog?.contact}
                     classificationOptions={lookups.classifications}
                     onClose={() => setContactDialog(null)} onDone={load} />
      <PropertyDialog key={propertyDialog?.property?.id || (propertyDialog ? "new-property" : "closed-property")}
                      open={propertyDialog !== null} prospectId={id} property={propertyDialog?.property}
                      segments={lookups.segments} occupancies={lookups.occupancies}
                      serviceStatuses={lookups.serviceStatuses} contacts={prospect.contacts || []}
                      onClose={() => setPropertyDialog(null)} onDone={load} />
      {bulkImportOpen && (
        <BulkImportDialog open prospect={prospect}
                          onClose={() => setBulkImportOpen(false)} onDone={load} />
      )}
    </div>
  );
}

